import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { getCanonicalSlug, productLink } from '@components/config/helpers';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckIcon from '@mui/icons-material/Check';
import TuneIcon from '@mui/icons-material/Tune';

/** How near the end of the list (in pixels) the scrolling has to come before the next batch is asked for. */
const LOAD_MORE_DISTANCE = 160;

const idOf = (product) => product.product_id || product.id || product.product_code;

/**
 * One product of the list. Memoised, and given only plain values and functions that never change, so adding the next batch to the
 * list (or the "Added" tick on one product) draws only the rows that are new or that changed, however long the list has grown.
 */
const SearchResultRow = React.memo(function SearchResultRow({ product, isAdding, isAdded, isMobile, onSelect, onOptions, onAdd }) {
    const productName = product.name || product.product_name || 'Product';
    const productCode = product.product_code || product.code || '';
    const unitPrice = product.unit_price ?? product.price ?? product.product_price;
    const hasFromPrice = Boolean(
        product.from_price !== null &&
        product.from_price !== undefined &&
        (product.price_breaks_count > 0 || product.has_price_breaks || (unitPrice && product.from_price < unitPrice))
    );
    const isPOA = product.poa === true || product.poa === 'yes' || product.poa_price === true || product.poa_price === 'yes';

    // A product on offer is shown as its category card shows it: the offer price (`discounted_price`) with the price struck through
    // beside it, and no "From". Without an offer price held, the product's own price is shown, as for a product not on offer.
    const isOnOffer = product.on_offer === 'yes' && !isPOA;
    const offerPrice = isOnOffer && Number(product.discounted_price) > 0 ? Number(product.discounted_price) : null;
    const originalPrice = offerPrice !== null && Number(product.product_price) > offerPrice ? `£${Number(product.product_price).toFixed(2)}` : null;

    const effectivePrice = hasFromPrice ? product.from_price : (product.price ?? product.product_price ?? product.unit_price);
    let formattedPrice = effectivePrice !== null && effectivePrice !== undefined && Number(effectivePrice) > 0
        ? `${hasFromPrice ? 'From ' : ''}£${Number(effectivePrice).toFixed(2)}`
        : 'POA';
    if (offerPrice !== null) formattedPrice = `£${offerPrice.toFixed(2)}`;
    const imageSrc = product.image_thumb || product.image || '/images/no-image.jpg';

    const isBlocked = isPOA || product.orderable === false || product.orderable === 'no';
    const hasExtras = Boolean(product.has_extras || product.product_extra_id || product.hasExtras);

    // The address of the product page, as every other link to a product is made. With no slug there is no address to make: the row is
    // then plain text and not a link to "undefined".
    const slug = product.slug || product.product_slug;
    const href = slug
        ? productLink(getCanonicalSlug({ canonical_category_slug: product.canonical_category_slug, section_slug: product.section_slug }), slug, product.long_term)
        : null;

    return (
        <Box
            role="option"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderBottom: '1px solid #f1f5f9',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                    bgcolor: '#f8fafc',
                },
            }}
        >
            {/* The image and the name are a real link to the product: its address shows on hover and it opens in a new tab. It is not
                prefetched, because a long list would otherwise fetch the page of every product in it. */}
            <Box
                component={href ? Link : 'div'}
                {...(href ? { href, prefetch: false } : {})}
                onClick={() => onSelect(product)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flex: 1,
                    minWidth: 0,
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: href ? 'pointer' : 'default',
                }}
            >
                <Box
                    component="img"
                    src={imageSrc}
                    alt={productName}
                    loading="lazy"
                    sx={{
                        width: 48,
                        height: 48,
                        objectFit: 'contain',
                        borderRadius: 1.5,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        p: 0.5,
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {productName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'nowrap' }}>
                        {productCode && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    bgcolor: '#f1f5f9',
                                    color: '#475569',
                                    px: 0.75,
                                    py: 0.2,
                                    borderRadius: 1,
                                    border: '1px solid #cbd5e1',
                                    letterSpacing: '0.02em',
                                    flexShrink: 0,
                                }}
                            >
                                {productCode}
                            </Box>
                        )}
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: offerPrice !== null ? 'error.main' : 'primary.main',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formattedPrice}
                        </Typography>
                        {originalPrice && (
                            <Typography
                                component="s"
                                aria-label={`was ${originalPrice}`}
                                sx={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}
                            >
                                {originalPrice}
                            </Typography>
                        )}
                        {isOnOffer && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: '#fff',
                                    bgcolor: 'error.main',
                                    px: 0.75,
                                    py: 0.2,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                }}
                            >
                                On offer
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                {isBlocked ? (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled
                        sx={{
                            textTransform: 'none',
                            fontSize: '11px',
                            px: 1,
                            py: 0.25,
                            minWidth: 'auto',
                        }}
                    >
                        POA
                    </Button>
                ) : hasExtras ? (
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
                        onClick={() => onOptions(product)}
                        aria-label={`Configure options for ${productName}`}
                        sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            px: 1.25,
                            py: 0.35,
                            borderRadius: '6px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Options
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant={isAdded ? "outlined" : "contained"}
                        color="success"
                        disabled={isAdding}
                        startIcon={isAdded ? <CheckIcon sx={{ fontSize: 16 }} /> : <AddShoppingCartIcon sx={{ fontSize: 16 }} />}
                        onClick={(event) => onAdd(event, product)}
                        aria-label={`Add ${productName} to quote`}
                        sx={{
                            textTransform: 'none',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 600,
                            px: isMobile ? 1 : 1.5,
                            py: 0.4,
                            borderRadius: '6px',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            bgcolor: isAdded ? 'transparent' : 'success.main',
                            color: isAdded ? 'success.main' : '#ffffff',
                            borderColor: isAdded ? 'success.main' : 'transparent',
                            '&:hover': {
                                bgcolor: isAdded ? 'rgba(46, 125, 50, 0.08)' : 'success.dark',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {isAdded ? 'Added ✓' : (isMobile ? '+ Quote' : 'Add to Quote')}
                    </Button>
                )}
            </Box>
        </Box>
    );
});

const ROW_SKELETON_KEYS = [1, 2, 3];

const RowSkeletons = () => (
    <Box sx={{ p: 1.5 }}>
        {ROW_SKELETON_KEYS.map((key) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="60%" height={20} />
                    <Skeleton variant="text" width="30%" height={16} />
                </Box>
                <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: 1 }} />
            </Box>
        ))}
    </Box>
);

/**
 * Floating search results dropdown displaying matching products as the user types.
 * Supports direct product navigation and inline Add to Quote actions.
 *
 * Every match can be reached by scrolling: the list asks for the next batch as the end comes near (`onLoadMore`), so only the
 * products that have been scrolled to are fetched and drawn. "Browse all" stays pinned at the foot of the list.
 *
 * @param {{
 *   open: boolean,
 *   query: string,
 *   loading: boolean,
 *   loadingMore?: boolean,
 *   moreFailed?: boolean,
 *   hasMore?: boolean,
 *   products: Array,
 *   total: number,
 *   error: string | null,
 *   onSelectProduct: (product: Object) => void,
 *   onBrowseAll: () => void,
 *   onLoadMore?: (options?: { retry?: boolean }) => void,
 *   onAddToQuote?: (product: Object) => Promise<void> | void,
 *   isMobile?: boolean
 * }} props
 */
const HeaderSearchDropdown = ({
    open,
    query,
    loading,
    loadingMore = false,
    moreFailed = false,
    hasMore = false,
    products = [],
    total = 0,
    error = null,
    onSelectProduct,
    onBrowseAll,
    onLoadMore,
    onAddToQuote,
    onOptionsClick,
    isMobile = false,
}) => {
    const [addingID, setAddingID] = useState(null);
    const [addedID, setAddedID] = useState(null);
    const listRef = useRef(null);

    // The rows are memoised, so what they are given must not change with every draw: functions that call the latest of the props.
    const latest = useRef({});
    latest.current = { onSelectProduct, onOptionsClick, onAddToQuote };

    const selectProduct = useCallback((product) => latest.current.onSelectProduct(product), []);

    const chooseOptions = useCallback((product) => {
        const { onOptionsClick: handleOptions, onSelectProduct: handleSelect } = latest.current;
        if (handleOptions) {
            handleOptions(product);
        } else {
            handleSelect(product);
        }
    }, []);

    const addToQuote = useCallback(async (event, product) => {
        event.stopPropagation();
        const productID = idOf(product);
        const handleAdd = latest.current.onAddToQuote;
        if (!productID || !handleAdd) {
            return;
        }

        setAddingID(productID);
        try {
            await handleAdd(product);
            setAddedID(productID);
            setTimeout(() => {
                setAddedID((current) => (current === productID ? null : current));
            }, 1800);
        } finally {
            setAddingID(null);
        }
    }, []);

    const wantsMore = Boolean(onLoadMore) && hasMore && !loading && !loadingMore && !moreFailed;

    const loadWhenNearEnd = useCallback(() => {
        const list = listRef.current;
        if (!list || !wantsMore) return;
        if (list.scrollHeight - list.scrollTop - list.clientHeight < LOAD_MORE_DISTANCE) onLoadMore();
    }, [wantsMore, onLoadMore]);

    // A batch that does not fill the list leaves nothing to scroll, so the next one is asked for without waiting for a scroll.
    useEffect(() => {
        const list = listRef.current;
        if (list && list.clientHeight > 0 && list.scrollHeight <= list.clientHeight) loadWhenNearEnd();
    }, [products.length, loadWhenNearEnd]);

    const trimmedQuery = (query || '').trim();

    if (!open || !trimmedQuery) {
        return null;
    }

    return (
        <Paper
            ref={listRef}
            onScroll={loadWhenNearEnd}
            elevation={8}
            sx={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: isMobile ? 8 : 0,
                right: isMobile ? 8 : 0,
                zIndex: 1250,
                maxHeight: isMobile ? 'min(400px, 75vh)' : 460,
                overflowY: 'auto',
                bgcolor: '#ffffff',
                color: '#1a202c',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
            }}
            role="listbox"
            aria-label="Search suggestions"
        >
            {loading && products.length === 0 && <RowSkeletons />}

            {error && !loading && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'error.main' }}>
                        {error}
                    </Typography>
                </Box>
            )}

            {!loading && !error && products.length === 0 && (
                <Box sx={{ p: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary' }}>
                        {`No products found for "${trimmedQuery}".`}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: 0.5 }}>
                        Try checking the spelling or searching for a broader term.
                    </Typography>
                </Box>
            )}

            {products.map((product) => {
                const productID = idOf(product);
                return (
                    <SearchResultRow
                        key={productID}
                        product={product}
                        isAdding={addingID === productID}
                        isAdded={addedID === productID}
                        isMobile={isMobile}
                        onSelect={selectProduct}
                        onOptions={chooseOptions}
                        onAdd={addToQuote}
                    />
                );
            })}

            {loadingMore && <RowSkeletons />}

            {moreFailed && !loadingMore && (
                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: 'error.main', mb: 0.5 }}>
                        Could not load more products.
                    </Typography>
                    <Button size="small" onClick={() => onLoadMore && onLoadMore({ retry: true })} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Try again
                    </Button>
                </Box>
            )}

            {total > 0 && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        p: 1.25,
                        bgcolor: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        textAlign: 'center',
                    }}
                >
                    <Button
                        fullWidth
                        size="small"
                        variant="text"
                        onClick={onBrowseAll}
                        sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            textTransform: 'none',
                            color: 'primary.main',
                            py: 0.5,
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        {`Browse all ${total} products ➔`}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default HeaderSearchDropdown;
